/**
 * Calendar-date helpers — for values that are DATES, not moments (birth dates,
 * form dates). `new Date('1965-08-19')` parses as UTC midnight, so any US
 * timezone renders it as Aug 18 — one day early. On medical-order pages a
 * patient's date of birth is an IDENTIFIER; it must match the record exactly
 * on every page.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(?:$|T00:00:00(?:\.0+)?(?:Z|\+00:?0?0?)?$)/;

/**
 * Parse a date string as a LOCAL calendar date when it carries no meaningful
 * time (plain `yyyy-MM-dd`, or midnight-UTC timestamps that databases emit for
 * date columns). Anything else parses as a normal timestamp.
 */
export function parseCalendarDate(value: string | Date): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  const match = DATE_ONLY.exec(value.trim());
  if (match) {
    return new Date(+match[1], +match[2] - 1, +match[3]);
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** "August 19, 1965" — the long form the clinician pages use. */
export function formatCalendarDate(value: string | Date): string {
  const date = parseCalendarDate(value);
  if (!date) {
    return '-';
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
